/**
 * Invoice types — what is specific to this feature on top of the shared rules
 * components (`stories/rules`): the fallback type, the line-item definitions
 * (not rules) and the invoicing API client.
 */
import type { AxiosInstance } from "axios";
import { DefinitionRecord, DefinitionsApi, restRulesApi, RULE_CONDITIONS, RuleRecord, RuleVocabulary, useRuleVocabulary } from "stories/rules";

export const INVOICE_TYPES_BASE = "/invoicing-v1/types";

export const INVOICE_TYPES_PERMISSION = {
  customerApplication: "Mercury",
  permissionName: "Invoice Types",
} as const;

export interface InvoiceType extends DefinitionRecord {
  id?: string;
  isFallback: boolean;
  clientId?: string;
}

export interface InvoiceTypeRule extends RuleRecord {
  invoicingType: string; // type _id
}

export interface InvoiceTypeCreate {
  name: string;
  priority: number;
  description?: string;
  isFallback?: boolean;
  active?: boolean;
  clientId: string;
  deleted: boolean;
}

export type InvoiceTypeUpdate = Partial<Pick<InvoiceType, "name" | "priority" | "description" | "isFallback" | "active" | "deleted">>;

/** What the editor offers when the backend does not serve `/types/vocabulary` yet. */
export const INVOICE_TYPE_RULE_VOCABULARY: RuleVocabulary = {
  targetEntities: ["DOCUMENT", "PLP", "PROJECT", "ENTITY"],
  conditionTypes: RULE_CONDITIONS,
};

export const TARGET_ENTITY_HELP: Record<string, string> = {
  ENTITY: "The whole fetched entity: the VAR entry with its document, PLP and project.",
  DOCUMENT: "The entity's document.",
  PLP: "The entity's PLP (project language pair).",
  PROJECT: "The entity's project.",
};

export type InvoiceTypesApi = DefinitionsApi<InvoiceType, InvoiceTypeRule, InvoiceTypeCreate, InvoiceTypeUpdate>;

const apis = new WeakMap<AxiosInstance, InvoiceTypesApi>();

/**
 * The invoicing API's rule endpoints share the common shape, except that its
 * rule schema still carries the owning type (`invoicingType`) and the soft
 * delete flag in the create body.
 */
export const invoiceTypesApi = (client: AxiosInstance): InvoiceTypesApi => {
  const cached = apis.get(client);
  if (cached) return cached;
  const api = restRulesApi<InvoiceType, InvoiceTypeRule, InvoiceTypeCreate, InvoiceTypeUpdate>(client, INVOICE_TYPES_BASE, {
    decorateRuleCreate: (typeId) => ({ invoicingType: typeId, deleted: false }),
    fallbackVocabulary: INVOICE_TYPE_RULE_VOCABULARY,
  });
  apis.set(client, api);
  return api;
};

export const useInvoiceTypeVocabulary = (client: AxiosInstance, onError?: (error: any) => void) =>
  useRuleVocabulary(invoiceTypesApi(client), onError);
